import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PercentIcon, CheckCircleIcon, XIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const TIPOS = [
  { v: "proveedor", l: "Proveedor" },
  { v: "partida", l: "Partida contable" },
  { v: "categoria", l: "Categoría de gasto" },
  { v: "linea", l: "Línea (texto)" },
];

function Shell({ title, icon, onClose, children, testid, wide, m: mExt }) {
  const mLocal = useModalForm({ onClose: mExt ? undefined : onClose, abierto: !mExt });
  const m = mExt || mLocal;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className={`bg-card border border-border rounded-lg w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`} data-testid={testid}>
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">{icon}{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        {children}
      </div>
    </div>
  );
}

// Editor de distribución % (proveedor / partida / categoría / línea). Debe sumar 100.
export function DistributionEditor({ items, setItems, monto }) {
  const [opts, setOpts] = useState({ proveedor: [], partida: [], categoria: [] });
  useEffect(() => {
    Promise.all([
      api.get("/suppliers").catch(() => ({ data: [] })),
      api.get("/accounting/expense-accounts").catch(() => ({ data: [] })),
      api.get("/expenses/categories").catch(() => ({ data: [] })),
    ]).then(([s, p, c]) => setOpts({
      proveedor: (s.data || []).map((x) => ({ id: x.id, l: `${x.codigo || ""} · ${x.nombre}` })),
      partida: (Array.isArray(p.data) ? p.data : p.data?.items || []).map((x) => ({ id: x.id, l: `${x.codigo} · ${x.nombre}` })),
      categoria: (c.data || []).map((x) => ({ id: x.id, l: `${x.codigo} · ${x.nombre}` })),
    }));
  }, []);
  const sum = items.reduce((s, i) => s + Number(i.porcentaje || 0), 0);
  const upd = (i, k, v) => { const l = [...items]; l[i] = { ...l[i], [k]: v, ...(k === "tipo" ? { id: "", nombre: "" } : {}) }; setItems(l); };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 bg-app rounded p-2 items-end">
          <div className="col-span-3"><label className="text-[9px] uppercase text-text-muted">Tipo</label>
            <select value={it.tipo} onChange={(e) => upd(i, "tipo", e.target.value)} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" data-testid={`dist-tipo-${i}`}>
              {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div className="col-span-5"><label className="text-[9px] uppercase text-text-muted">Destino</label>
            {it.tipo === "linea" ? (
              <input value={it.nombre || ""} onChange={(e) => { const l = [...items]; l[i] = { ...l[i], nombre: e.target.value, id: e.target.value }; setItems(l); }} placeholder="Ej. Semillas" className="w-full bg-card border border-border rounded px-2 py-1 text-xs" />
            ) : (
              <select value={it.id} onChange={(e) => upd(i, "id", e.target.value)} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" data-testid={`dist-sup-${i}`}>
                <option value="">— Seleccionar —</option>
                {(opts[it.tipo] || []).map((o) => <option key={o.id} value={o.id}>{o.l}</option>)}
              </select>
            )}
          </div>
          <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted">%</label>
            <input type="number" step="0.1" value={it.porcentaje} onChange={(e) => upd(i, "porcentaje", e.target.value)} className="w-full bg-card border border-border rounded px-2 py-1 text-xs text-right" data-testid={`dist-pct-${i}`} />
          </div>
          <div className="col-span-2 text-right text-[10px] text-text-muted">
            {fmtL((Number(monto) || 0) * Number(it.porcentaje || 0) / 100)}
            <button type="button" onClick={() => setItems(items.filter((_, x) => x !== i))} className="ml-2 text-red-400 p-1.5" aria-label="Quitar línea"><XIcon size={14} /></button>
          </div>
        </div>
      ))}
      <div className="flex justify-between items-center">
        <button type="button" onClick={() => setItems([...items, { tipo: "proveedor", id: "", porcentaje: items.length ? 0 : 100 }])} className="text-xs text-agro-green" data-testid="dist-add">+ Agregar destino</button>
        {items.length > 0 && <span className={`text-xs font-mono ${Math.abs(sum - 100) > 0.01 ? "text-red-400" : "text-agro-green"}`}>Total: {sum.toFixed(2)}%</span>}
      </div>
    </div>
  );
}

const toPayload = (items) => items.map((it) => ({ tipo: it.tipo || "proveedor", id: it.id || it.proveedor_id || "", nombre: it.nombre || it.proveedor_nombre || "", porcentaje: Number(it.porcentaje || 0) }));
const validDist = (items) => {
  if (!items.length) return true;
  const sum = items.reduce((s, i) => s + Number(i.porcentaje || 0), 0);
  if (Math.abs(sum - 100) > 0.01) { toast.error(`La suma debe ser 100%. Actual: ${sum.toFixed(2)}%`); return false; }
  if (items.some((i) => !(i.id || i.nombre))) { toast.error("Complete el destino de cada línea"); return false; }
  return true;
};
const initialDist = (e) => (e.distribucion?.length ? e.distribucion
  : (e.distribucion_proveedores || []).map((d) => ({ tipo: "proveedor", id: d.proveedor_id, nombre: d.proveedor_nombre, porcentaje: d.porcentaje })));

// Aprobación con comentario + distribución en el mismo paso (reemplaza prompt()).
export function ApproveModal({ expense, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [items, setItems] = useState(initialDist(expense));
  const [saving, setSaving] = useState(false);
  const metodoTxt = { efectivo_propio: "Dr gasto / Cr Reembolsos por pagar a empleados (queda pendiente de reembolso)",
    contra_adelanto: "Dr gasto / Cr 1107 Anticipos (liquida el anticipo)", tarjeta_empresa: "Dr gasto / Cr tarjeta de crédito (o banco según configuración)" }[expense.metodo_pago];
  const submit = async (e) => {
    e.preventDefault();
    if (!validDist(items)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/expenses/${expense.id}/approve`, { motivo, distribucion: toPayload(items) });
      toast.success("Gasto aprobado y contabilizado");
      onDone(data);
    } catch (er) { toast.error(apiErr(er)); } finally { setSaving(false); }
  };
  const am = useModalForm({ value: { motivo, items }, onClose, onSave: submit });
  return (
    <Shell title={`Aprobar ${expense.numero}`} icon={<CheckCircleIcon size={20} className="text-agro-green" />} onClose={onClose} m={am} testid="approve-modal" wide>
      <form onSubmit={am.guardar} className="p-5 space-y-3">
        <div className="bg-app rounded p-3 text-xs space-y-1">
          <div>Solicitante: <b>{expense.solicitante_nombre}</b> · Monto: <b className="text-agro-green">{fmtL(expense.monto)}</b></div>
          <div className="text-text-muted">Asiento: {metodoTxt}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Comentario / motivo de aprobación</label>
          <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="approve-motivo" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-agro-yellow mb-1.5 flex items-center gap-1"><PercentIcon size={12} /> Distribución % (opcional, debe sumar 100)</div>
          <DistributionEditor items={items} setItems={setItems} monto={expense.monto} />
        </div>
        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <button type="button" onClick={am.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
          <button type="submit" disabled={(saving) || am.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="approve-confirm">{saving ? "…" : "Aprobar"}</button>
        </div>
      </form>
    </Shell>
  );
}

// Solo distribución (gasto enviado/aprobado).
export function DistributionModal({ expense, onClose, onSaved }) {
  const [items, setItems] = useState(initialDist(expense).length ? initialDist(expense) : [{ tipo: "proveedor", id: "", porcentaje: 100 }]);
  const save = async (e) => {
    e.preventDefault();
    if (!validDist(items)) return;
    try {
      const { data } = await api.post(`/expenses/${expense.id}/distribution`, { distribucion: toPayload(items) });
      toast.success("Distribución guardada");
      onSaved(data);
    } catch (er) { toast.error(apiErr(er)); }
  };
  const dm = useModalForm({ value: items, onClose, onSave: save });
  return (
    <Shell title="Distribución %" icon={<PercentIcon size={20} className="text-agro-yellow" />} onClose={onClose} m={dm} testid="dist-modal" wide>
      <form onSubmit={dm.guardar} className="p-5 space-y-3">
        <div className="text-xs text-text-muted bg-app rounded p-2">Asigna el % del gasto ({fmtL(expense.monto)}) por proveedor, partida, categoría o línea. Alimenta los indicadores de inversión.</div>
        <DistributionEditor items={items} setItems={setItems} monto={expense.monto} />
        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <button type="button" onClick={dm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
          <button disabled={dm.saving} type="submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="dist-save">Guardar distribución</button>
        </div>
      </form>
    </Shell>
  );
}

// Motivo obligatorio para denegar / devolver.
export function MotivoModal({ title, label, confirmLabel, danger, onClose, onConfirm }) {
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!motivo.trim()) { toast.error("El motivo es obligatorio"); return; }
    setSaving(true);
    try { await onConfirm(motivo.trim()); } finally { setSaving(false); }
  };
  const mm = useModalForm({ value: motivo, onClose, onSave: submit });
  return (
    <Shell title={title} onClose={onClose} m={mm} testid="motivo-modal">
      <form onSubmit={mm.guardar} className="p-5 space-y-3">
        <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium block">{label}</label>
        <textarea autoFocus rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" data-testid="motivo-input" />
        <div className="flex justify-end gap-2 border-t border-border/60 pt-3">
          <button type="button" onClick={mm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
          <button type="submit" disabled={(saving) || mm.saving} className={`px-5 py-2 text-sm font-medium rounded-md ${danger ? "bg-red-500 text-white" : "bg-orange-500 text-black"}`} data-testid="motivo-confirm">{confirmLabel}</button>
        </div>
      </form>
    </Shell>
  );
}
